import { useState, useEffect } from "react";
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import ProductFormModal from "./components/ProductFormModal";
import ProductsPage from "./components/ProductsPage";
import DataPage from "./components/DataPage";
import Charts from "./components/Charts";
import BlogsPage from "./components/BlogsPage";
import UserManagement from "./components/UserManagement";
import Login from "./components/Login";
import Signup from "./components/Signup";
import { ThemeProvider, useTheme } from "./components/ThemeContext";
import { AuthProvider, useAuth } from "./components/AuthContext";
import { ProductProvider } from "./components/ProductContext";
import { BlogProvider } from "./components/BlogContext";
import { useToast } from "./components/Toast";
import "./App.css";

function AppContent() {
  const [collapsed, setCollapsed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [currentPage, setCurrentPage] = useState("dashboard");
  const [showLogin, setShowLogin] = useState(true);
  const { isDarkMode } = useTheme();
  const { addToast, ToastContainer } = useToast();
  const { user, loading, logout } = useAuth();

  // Reset to dashboard whenever user logs in
  useEffect(() => {
    if (user) {
      // Always set to dashboard after login
      setCurrentPage("dashboard");
      localStorage.setItem("activeSidebarItem", "dashboard");
    }
  }, [user]);

  // Load saved page only if it exists and user is logged in
  useEffect(() => {
    if (user) {
      const savedPage = localStorage.getItem("activeSidebarItem");
      // Only load saved page if it's not login/signup/addproduct and user is logged in
      if (savedPage && savedPage !== "addproduct" && savedPage !== "login" && savedPage !== "signup") {
        setCurrentPage(savedPage);
      } else {
        setCurrentPage("dashboard");
      }
    }
  }, [user]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (page !== "addproduct") {
      localStorage.setItem("activeSidebarItem", page);
    }
    if (page === "addproduct") {
      setIsModalOpen(true);
      setEditingProductId(null);
    }
    addToast(`Navigated to ${page}`, 'info', 2000);
  };

  const handleEditProduct = (productId) => {
    setEditingProductId(productId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProductId(null);
    setCurrentPage("products");
    localStorage.setItem("activeSidebarItem", "products");
    addToast('Product modal closed', 'info', 2000);
  };

  const handleAddProductClick = () => {
    setEditingProductId(null);
    setIsModalOpen(true);
  };

  const renderContent = () => {
    switch(currentPage) {
      case "data":
        return <DataPage addToast={addToast} />;
      case "dashboard":
        return <Charts addToast={addToast} />;
      case "products":
        return <ProductsPage 
          onEditProduct={handleEditProduct} 
          onAddProductClick={handleAddProductClick}
        />;
      case "blogs":
        return <BlogsPage addToast={addToast} />;
      case "users":
        return <UserManagement addToast={addToast} />;
      default:
        return (
          <div className={`dashboard-content ${isDarkMode ? 'dark' : ''}`}>
            <h2>{currentPage}</h2>
            <p>Content for {currentPage} goes here</p>
          </div>
        );
    }
  };

  // Show login/signup if not authenticated
  if (!user && !loading) {
    return (
      <div className={`auth-wrapper ${isDarkMode ? 'dark-mode' : ''}`}>
        {showLogin ? (
          <Login onSwitch={() => setShowLogin(false)} />
        ) : (
          <Signup onSwitch={() => setShowLogin(true)} />
        )}
        <ToastContainer />
      </div>
    );
  }

  // Show loading
  if (loading) {
    return (
      <div className={`loading-container ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Show dashboard if authenticated
  return (
    <div className={`layout ${isDarkMode ? 'dark-mode' : ''}`}>
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        onAddProductClick={handleAddProductClick}
        onPageChange={handlePageChange}
        currentPage={currentPage}
        onLogout={logout}
      />

      <div className={`main ${collapsed ? 'collapsed' : ''}`}>
        <Header collapsed={collapsed} />
        <div className={`content ${isDarkMode ? 'dark' : ''}`}>
          {renderContent()}
        </div>
      </div>

      <ProductFormModal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal}
        editProductId={editingProductId}
      />
      
      <ToastContainer />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ProductProvider>
          <BlogProvider>
            <AppContent />
          </BlogProvider>
        </ProductProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;